import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { ENV } from "@app/constants/env";
import { getToken } from "@app/helpers/authProvider";

type FileAttachment = {
  FileAttachmentId: number;
  FileAttachments: string;
  PreAlertID: number;
  UserID: number;
  blobUrl:string;
};

type ApiResponse<T> = {
  data: T;
  success: boolean;
  message?: string;
};

const API_BASE_URL = ENV.BASE_API_URL +ENV.JOB_APIURL;

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
});

const authBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (incomingArgs, api, extraOptions) => {
  let args: string | FetchArgs = incomingArgs;

  if (typeof args === "string") {
    args = { url: args };
  }

  try {
    const token = await getToken();
    if (token) {
      const headers =
        (args as FetchArgs).headers instanceof Headers
          ? (args as FetchArgs).headers
          : new Headers((args as FetchArgs).headers);

      headers.set("Authorization", `Bearer ${token}`);
      (args as FetchArgs).headers = headers;
    }

    const result = await rawBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401 && token) {
      // refresh token flow could go here
    }

    return result;
  } catch {
    return {
      error: {
        status: 500,
        data: "Token retrieval failed",
      },
    } as FetchBaseQueryError;
  }
};

// Normalizes API responses that may be wrapped with a { data, success } envelope
function normalizeApiResponse<T>(response: any): ApiResponse<T> {
  if (response && typeof response === "object" && "data" in response) {
    return response as ApiResponse<T>;
  }
  return { data: response as T, success: true };
}

export const fileAttachmentApiSlice = createApi({
  reducerPath: "efairsJobFileAttachmentApi",
  baseQuery: authBaseQuery,
  tagTypes: ["FileAttachment"],
  endpoints: (builder) => ({
    getAllPreAlertFileAttachments: builder.mutation<
      ApiResponse<FileAttachment[]>,
      FileAttachment
    >({
      query: (fileAttachmentBody) => ({
        url: "/FileAttachments/GetAllPreAlertFileAttachments",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: fileAttachmentBody,
      }),
      invalidatesTags: ["FileAttachment"],
      transformResponse: (response: any) => normalizeApiResponse<FileAttachment[]>(response),
    }),
   
    insertPreAlertFileAttachments: builder.mutation<
      ApiResponse<FileAttachment>,
      FileAttachment
    >({
      query: (fileAttachmentBody) => ({
        url: "/FileAttachments/InsertPreAlertFileAttachments",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: fileAttachmentBody,
      }),
      invalidatesTags: ["FileAttachment"],
      transformResponse: (response: any) => normalizeApiResponse<FileAttachment>(response),
    }),
    deletePreAlertFileAttachment: builder.mutation<
      ApiResponse<void>,
      FileAttachment
    >({
      query: (fileAttachmentBody) => ({
        url: "/FileAttachments/DeletePreAlertFileAttachment",
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: fileAttachmentBody,
      }),
      invalidatesTags: ["FileAttachment"],
    }),

    getDMSFileAttachments: builder.query<
      ApiResponse<FileAttachment[]>,
      string
    >({
      query: (preAlertID) => ({
        url: `/FileAttachments/GetDMSFileAttachments/${preAlertID}`,
        method: "GET",
      }),
      providesTags: ["FileAttachment"],
      transformResponse: (response: any) => normalizeApiResponse<FileAttachment[]>(response),
    }),
  }),
});

export const {
  useGetAllPreAlertFileAttachmentsMutation,
  useInsertPreAlertFileAttachmentsMutation,
  useDeletePreAlertFileAttachmentMutation,
  useGetDMSFileAttachmentsQuery,
} = fileAttachmentApiSlice;
